import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { ArrowLeft } from "lucide-react";
import { Toaster } from "./ui/sonner";
import Header from "./Header";

const mockTeams = [
  { team_id: 10, team_name: "Milan", team_short: "MIL" },
  { team_id: 6, team_name: "Genoa", team_short: "GEN" },
  { team_id: 8, team_name: "Inter", team_short: "INT" },
  { team_id: 17, team_name: "Lazio", team_short: "LAZ" },
  { team_id: 4, team_name: "Atalanta", team_short: "ATA" }
];

const mockMatches = [
  { event_unit_id: 27, home_team_id: 10, away_team_id: 6 },
  { event_unit_id: 26, home_team_id: 8, away_team_id: 10 },
  { event_unit_id: 25, home_team_id: 10, away_team_id: 17 },
  { event_unit_id: 24, home_team_id: 4, away_team_id: 10 }
];

const mockPlayerById = {
  "9001": {
    athlete: {
      athlete_id: 9001,
      first_name: "Luka",
      last_name: "Modric",
      athlete_shortname: "MODRIC L.",
      position: "Midfielder",
      pos_short: "MID",
      age: 40,
      nationality: "Croatia",
      team_id: 10,
      squad_number: 14,
      picture: "https://images.unsplash.com/photo-1522778526097-ce0a22ceb253?auto=format&fit=crop&w=300&q=80",
      active: 1
    },
    athlete_eventunit_participation: [
      {
        aep_id: 50211,
        athlete_id: 9001,
        event_unit_id: 27,
        team_id: 10,
        athlete_unit_bonus_indv: 0.5,
        athlete_unit_points_indv: 3.0,
        athlete_unit_points_team: 1.0,
        athlete_unit_points: 4.5,
        is_ended: 1,
        event_unit_cost: 4.2,
        status: "available"
      },
      {
        aep_id: 50081,
        athlete_id: 9001,
        event_unit_id: 26,
        team_id: 10,
        athlete_unit_bonus_indv: 0.0,
        athlete_unit_points_indv: 2.0,
        athlete_unit_points_team: 0.5,
        athlete_unit_points: 2.5,
        is_ended: 1,
        event_unit_cost: 4.0,
        status: "available"
      },
      {
        aep_id: 49942,
        athlete_id: 9001,
        event_unit_id: 25,
        team_id: 10,
        athlete_unit_bonus_indv: 1.0,
        athlete_unit_points_indv: 3.5,
        athlete_unit_points_team: 1.0,
        athlete_unit_points: 5.5,
        is_ended: 1,
        event_unit_cost: 3.8,
        status: "available"
      },
      {
        aep_id: 49760,
        athlete_id: 9001,
        event_unit_id: 24,
        team_id: 10,
        athlete_unit_bonus_indv: -0.5,
        athlete_unit_points_indv: 1.5,
        athlete_unit_points_team: 0.0,
        athlete_unit_points: 1.0,
        is_ended: 1,
        event_unit_cost: 3.6,
        status: "available"
      }
    ]
  }
};

const pageContent = {
  it: {
    nav: {
      signIn: "Accedi",
      register: "Registrati",
      menu: {
        rules: "Regolamento",
        community: "Community",
        topPlayers: "Top Players",
        playerValues: "Prezzi Giocatori",
        download: "Scarica l'App",
        contact: "Contatti"
      }
    },
    footer: { rights: "Tutti i diritti riservati." },
    meta: {
      title: "Scheda Giocatore | Fanteex",
      description: "Dettaglio giocatore con anagrafica, costo e storico matchday."
    },
    page: {
      badge: "Scheda Giocatore",
      statsTitle: "Statistiche",
      backToTop: "Top Players",
      backToCosts: "Prezzi Giocatori",
      notFound: "Giocatore non trovato.",
      loading: "Caricamento scheda giocatore...",
      liveError: "Errore nel caricamento dei dati live.",
      showingMock: "Stai vedendo dati di test.",
      fields: {
        team: "Squadra",
        age: "Eta",
        nationality: "Nazionalita",
        number: "Numero",
        apps: "Presenze",
        totFxPts: "Fanteex Point Totali",
        avFxPts: "Media FX Points",
        totFxBns: "Fanteex Bonus",
        avFxBns: "Media Fanteex Bonus"
      },
      table: {
        title: "Storico Matchday",
        eventUnit: "Giornata",
        match: "Partita",
        cost: "Costo",
        bonus: "Bonus indv",
        pointsIndv: "Punti indv",
        pointsTeam: "Punti team",
        pointsTotal: "Punti tot",
        official: "Dato Ufficiale"
      }
    }
  },
  en: {
    nav: {
      signIn: "Sign In",
      register: "Register",
      menu: {
        rules: "Game Rules",
        community: "Community",
        topPlayers: "Top Players",
        playerValues: "Player Costs",
        download: "Download App",
        contact: "Get in Touch"
      }
    },
    footer: { rights: "All rights reserved." },
    meta: {
      title: "Player Profile | Fanteex",
      description: "Player detail with profile fields, costs and matchday history."
    },
    page: {
      badge: "Player Profile",
      statsTitle: "Statistics",
      backToTop: "Top Players",
      backToCosts: "Player Costs",
      notFound: "Player not found.",
      loading: "Loading player profile...",
      liveError: "Failed to load live data.",
      showingMock: "You are viewing test data.",
      fields: {
        team: "Team",
        age: "Age",
        nationality: "Nationality",
        number: "Number",
        apps: "Apps",
        totFxPts: "Total Fanteex Points",
        avFxPts: "Avg FX Points",
        totFxBns: "Total Fanteex Bonus",
        avFxBns: "Avg Fanteex Bonus"
      },
      table: {
        title: "Matchday History",
        eventUnit: "Matchday",
        match: "Match",
        cost: "Cost",
        bonus: "Indv bonus",
        pointsIndv: "Indv points",
        pointsTeam: "Team points",
        pointsTotal: "Total points",
        official: "Offficial"
      }
    }
  }
};

const API_CANDIDATE_BASES = [
  "",
  "https://app.fanteex.com"
];

const TechBackground = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
    <div className="absolute top-[-20%] left-[-20%] w-[80vw] h-[80vw] bg-[#1a0b2e] rounded-full blur-[100px] opacity-80" />
    <div className="absolute bottom-[-20%] right-[-20%] w-[80vw] h-[80vw] bg-[#0f0518] rounded-full blur-[100px] opacity-90" />
    <div className="absolute inset-0 bg-[linear-gradient(rgba(93,253,203,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(93,253,203,0.03)_1px,transparent_1px)] bg-[size:100px_100px]" />
  </div>
);

const positionMap = {
  G: "GK",
  D: "DEF",
  M: "MID",
  A: "ATT",
  F: "ATT",
  GOALKEEPER: "GK",
  PORTIERE: "GK",
  DEFENDER: "DEF",
  DIFENSORE: "DEF",
  MIDFIELDER: "MID",
  CENTROCAMPISTA: "MID",
  FORWARD: "ATT",
  STRIKER: "ATT",
  ATTACKER: "ATT",
  ATTACCANTE: "ATT"
};

const roleNames = {
  it: { GK: "Portiere", DEF: "Difensore", MID: "Centrocampista", ATT: "Attaccante" },
  en: { GK: "Goalkeeper", DEF: "Defender", MID: "Midfielder", ATT: "Forward" }
};

const roleBadgeByLang = {
  it: { GK: "P", DEF: "D", MID: "C", ATT: "A" },
  en: { GK: "G", DEF: "D", MID: "M", ATT: "A" }
};

const normalizePosition = (value) => {
  if (!value) return "";
  const raw = String(value).trim().toUpperCase();
  return positionMap[raw] || raw;
};

const getRoleDotClass = (position) => {
  const normalized = normalizePosition(position);
  const map = {
    GK: "bg-amber-400 text-black",
    DEF: "bg-sky-400 text-black",
    MID: "bg-emerald-400 text-black",
    ATT: "bg-rose-400 text-black"
  };
  return map[normalized] || "bg-white/20 text-white";
};

const formatNumber = (value, decimals = 1) => {
  if (value === null || value === undefined) return "-";
  return Number(value).toFixed(decimals);
};

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const slugifyPlayerName = (value) => {
  const base = String(value || "player")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
  return base || "player";
};

const PlayerProfilePage = () => {
  const [lang, setLang] = useState("it");
  const [playerData, setPlayerData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isNotFound, setIsNotFound] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [usingMockData, setUsingMockData] = useState(false);
  const { athleteId, athleteSlug } = useParams();
  const t = pageContent[lang];

  const toggleLanguage = () => {
    setLang((prev) => (prev === "it" ? "en" : "it"));
  };

  useEffect(() => {
    let isMounted = true;

    const loadPlayerProfile = async () => {
      if (!athleteId) {
        if (isMounted) {
          setPlayerData(null);
          setIsLoading(false);
        }
        return;
      }

      setIsLoading(true);
      setIsNotFound(false);
      setLoadError("");
      setUsingMockData(false);

      const encodedAthleteId = encodeURIComponent(String(athleteId));
      const urls = API_CANDIDATE_BASES.map(
        (base) => `${base}/api/players/${encodedAthleteId}/profile`
      );

      let lastErrorMessage = "";
      let got404 = false;

      for (const url of urls) {
        try {
          const response = await fetch(url);
          if (response.status === 404) {
            got404 = true;
            continue;
          }
          if (!response.ok) {
            throw new Error(`Request failed: ${response.status}`);
          }

          const data = await response.json();
          if (isMounted) {
            setPlayerData(data);
            setIsLoading(false);
          }
          return;
        } catch (error) {
          lastErrorMessage = error?.message || "Load error";
        }
      }

      if (!isMounted) return;

      const fallbackMock = mockPlayerById[String(athleteId)] || null;
      if (fallbackMock) {
        setPlayerData(fallbackMock);
        setUsingMockData(true);
        setLoadError(lastErrorMessage);
      } else {
        setPlayerData(null);
        setIsNotFound(got404);
        setLoadError(lastErrorMessage);
      }
      setIsLoading(false);
    };

    loadPlayerProfile();

    return () => {
      isMounted = false;
    };
  }, [athleteId]);

  const athlete = playerData?.athlete || null;

  const participationRows = useMemo(() => {
    if (!playerData) return [];

    const baseRows = Array.isArray(playerData.participation)
      ? playerData.participation
      : Array.isArray(playerData.athlete_eventunit_participation)
        ? playerData.athlete_eventunit_participation
        : [];

    return [...baseRows]
      .map((row, index) => {
        let homeTeamShort = row.home_team_short || "";
        let awayTeamShort = row.away_team_short || "";

        if ((!homeTeamShort || !awayTeamShort) && row.match_code) {
          const matchParts = String(row.match_code).split("-");
          homeTeamShort = homeTeamShort || (matchParts[0] || "").trim();
          awayTeamShort = awayTeamShort || (matchParts[1] || "").trim();
        }

        if ((!homeTeamShort || !awayTeamShort) && row.match) {
          const matchParts = String(row.match).split("-");
          homeTeamShort = homeTeamShort || (matchParts[0] || "").trim();
          awayTeamShort = awayTeamShort || (matchParts[1] || "").trim();
        }

        if ((!homeTeamShort || !awayTeamShort) && row.event_unit_id) {
          const fallbackMatch = mockMatches.find(
            (item) =>
              Number(item.event_unit_id) === Number(row.event_unit_id) &&
              (Number(item.home_team_id) === Number(row.team_id) || Number(item.away_team_id) === Number(row.team_id))
          );
          const homeTeam = mockTeams.find((team) => Number(team.team_id) === Number(fallbackMatch?.home_team_id));
          const awayTeam = mockTeams.find((team) => Number(team.team_id) === Number(fallbackMatch?.away_team_id));
          homeTeamShort = homeTeamShort || homeTeam?.team_short || "";
          awayTeamShort = awayTeamShort || awayTeam?.team_short || "";
        }

        const fallbackTeam = mockTeams.find((team) => Number(team.team_id) === Number(row.team_id || athlete?.team_id));
        const teamName = row.team_name || athlete?.team_name || fallbackTeam?.team_name || "-";

        let playerTeamShort = row.team_short || athlete?.team_short || "";
        if (!playerTeamShort) {
          if (row.player_team_is_home === true) {
            playerTeamShort = homeTeamShort;
          } else if (row.player_team_is_home === false) {
            playerTeamShort = awayTeamShort;
          } else if (row.team_id && row.home_team_id && Number(row.team_id) === Number(row.home_team_id)) {
            playerTeamShort = homeTeamShort;
          } else if (row.team_id && row.away_team_id && Number(row.team_id) === Number(row.away_team_id)) {
            playerTeamShort = awayTeamShort;
          }
        }
        playerTeamShort = playerTeamShort || fallbackTeam?.team_short || "";

        return {
          ...row,
          _row_key: row.aep_id || `${row.event_unit_id || "eu"}-${index}`,
          home_team_short: homeTeamShort,
          away_team_short: awayTeamShort,
          player_team_short: playerTeamShort,
          team_name: teamName
        };
      })
      .sort((a, b) => Number(b.event_unit_id ?? -1) - Number(a.event_unit_id ?? -1));
  }, [playerData, athlete]);

  const athleteTeam = athlete?.team_name
    ? { team_name: athlete.team_name, team_short: athlete.team_short || "" }
    : mockTeams.find((team) => Number(team.team_id) === Number(athlete?.team_id));
  const normalizedRole = normalizePosition(athlete?.pos_short || athlete?.position || "");
  const roleLabel = roleNames[lang]?.[normalizedRole] || athlete?.position || "-";
  const roleBadge = roleBadgeByLang[lang]?.[normalizedRole] || normalizedRole?.[0] || "-";

  const playerEventStats = useMemo(() => {
    const statsRaw = playerData?.player_event_stats
      || playerData?.event_stats
      || playerData?.stats
      || athlete?.player_event_stats
      || null;

    const normalizedStats = Array.isArray(statsRaw) ? (statsRaw[0] || null) : statsRaw;

    const sumPoints = participationRows.reduce((sum, row) => {
      const value = toNumberOrNull(row.athlete_unit_points);
      return sum + (value ?? 0);
    }, 0);

    const sumBonus = participationRows.reduce((sum, row) => {
      const value = toNumberOrNull(row.athlete_unit_bonus_indv);
      return sum + (value ?? 0);
    }, 0);

    const apps = toNumberOrNull(normalizedStats?.apps) ?? participationRows.length;
    const totFxPts = toNumberOrNull(normalizedStats?.tot_fx_pts) ?? (participationRows.length > 0 ? sumPoints : null);
    const avFxPts = toNumberOrNull(normalizedStats?.av_fx_pts)
      ?? (apps > 0 && totFxPts !== null ? totFxPts / apps : null);
    const totFxBns = toNumberOrNull(normalizedStats?.tot_fx_bns) ?? (participationRows.length > 0 ? sumBonus : null);
    const avFxBns = toNumberOrNull(normalizedStats?.av_fx_bns)
      ?? (apps > 0 && totFxBns !== null ? totFxBns / apps : null);

    return {
      apps,
      totFxPts,
      avFxPts,
      totFxBns,
      avFxBns
    };
  }, [playerData, athlete, participationRows]);

  const canonicalNameSource = athlete
    ? `${athlete.first_name || ""} ${athlete.last_name || ""}`.trim() || athlete.athlete_shortname
    : athleteSlug;
  const canonicalSlug = slugifyPlayerName(canonicalNameSource);
  const canonicalUrl = `https://fanteex.com/player/${encodeURIComponent(String(athleteId || ""))}/${canonicalSlug}`;

  const renderMatchCode = (row) => {
    if (!row.home_team_short || !row.away_team_short) return "-";
    return (
      <>
        <span className={row.player_team_short === row.home_team_short ? "font-bold text-white" : ""}>
          {row.home_team_short}
        </span>
        <span>-</span>
        <span className={row.player_team_short === row.away_team_short ? "font-bold text-white" : ""}>
          {row.away_team_short}
        </span>
      </>
    );
  };

  return (
    <>
      <Helmet>
        <title>{t.meta.title}</title>
        <meta name="description" content={t.meta.description} />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>

      <div className="relative min-h-screen bg-fanteex-dark overflow-x-hidden flex flex-col font-sans selection:bg-[#5DFDCB] selection:text-fanteex-dark">
        <div className="fixed inset-0 z-0 pointer-events-none bg-[#0f0518]">
          <div className="absolute inset-0 opacity-15 mix-blend-overlay" style={{backgroundImage:"url('/assets/noise.svg')"}}></div>
        </div>
        <TechBackground />
        <Header t={t} lang={lang} toggleLanguage={toggleLanguage} />

        <main className="flex-1 pt-28 pb-16 px-4 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="mb-6 flex flex-wrap items-center gap-3">
              <Link
                to="/top-players"
                className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm font-display font-bold uppercase tracking-wide text-white/80 hover:text-white hover:border-[#5DFDCB]/60 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                {t.page.backToTop}
              </Link>
              <Link
                to="/players-costs"
                className="inline-flex items-center rounded-full border border-white/20 px-4 py-2 text-sm font-display font-bold uppercase tracking-wide text-white/80 hover:text-white hover:border-[#5DFDCB]/60 transition-colors"
              >
                {t.page.backToCosts}
              </Link>
            </div>

            {isLoading && (
              <div className="bg-[#140623]/80 backdrop-blur-xl border border-white/10 rounded-2xl p-10 text-center text-white/70 font-sans">
                {t.page.loading}
              </div>
            )}

            {!isLoading && !athlete && (
              <div className="bg-[#140623]/80 backdrop-blur-xl border border-white/10 rounded-2xl p-10 text-center text-white/70 font-sans">
                {t.page.notFound}
                {!!loadError && !isNotFound && <div className="mt-2 text-xs text-red-300">{loadError}</div>}
              </div>
            )}

            {!isLoading && athlete && (
              <>
                {(usingMockData || loadError) && (
                  <div className="mb-4 rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-xs text-amber-200 font-sans">
                    {t.page.liveError}
                    {usingMockData && <span className="ml-1">{t.page.showingMock}</span>}
                    {!!loadError && <span className="ml-1">({loadError})</span>}
                  </div>
                )}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.5 }}
                  className="bg-[#140623]/80 backdrop-blur-xl border border-white/10 rounded-2xl p-6 md:p-8 mb-6"
                >
                  <div className="flex flex-col md:flex-row md:items-center gap-6">
                    <div className="flex items-center gap-4 min-w-0">
                      {athlete.picture ? (
                        <img
                          src={athlete.picture}
                          alt={athlete.athlete_shortname || "Player"}
                          className="w-20 h-20 rounded-full object-cover border border-[#5DFDCB]/30"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-20 h-20 rounded-full bg-white/10 border border-[#5DFDCB]/20" />
                      )}
                      <div className="min-w-0">
                        <div className="text-[#5DFDCB] font-display font-bold uppercase tracking-[0.2em] text-xs mb-1">
                          {t.page.badge}
                        </div>
                        <h1 className="text-3xl md:text-5xl font-display font-black text-white uppercase leading-tight">
                          {athlete.first_name} {athlete.last_name}
                        </h1>
                        <div className="text-white/60 font-sans text-sm uppercase tracking-wide">
                          {athlete.athlete_shortname}
                        </div>
                      </div>
                    </div>

                    <div className="md:ml-auto flex items-center gap-3">
                      <span
                        className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-display font-bold ${getRoleDotClass(normalizedRole)}`}
                      >
                        {roleBadge}
                      </span>
                      <span className="text-white/70 font-sans text-sm">{roleLabel}</span>
                    </div>
                  </div>

                  <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                      <div className="text-white/50 text-xs font-display uppercase">{t.page.fields.team}</div>
                      <div className="text-white font-display font-bold mt-1">{athleteTeam?.team_name || "-"}</div>
                    </div>
                    <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                      <div className="text-white/50 text-xs font-display uppercase">{t.page.fields.age}</div>
                      <div className="text-white font-display font-bold mt-1">{athlete.age || "-"}</div>
                    </div>
                    <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                      <div className="text-white/50 text-xs font-display uppercase">{t.page.fields.nationality}</div>
                      <div className="text-white font-display font-bold mt-1">{athlete.nationality || "-"}</div>
                    </div>
                    <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                      <div className="text-white/50 text-xs font-display uppercase">{t.page.fields.number}</div>
                      <div className="text-white font-display font-bold mt-1">{athlete.squad_number || "-"}</div>
                    </div>
                  </div>

                  <div className="mt-6 mb-3">
                    <h3 className="text-[#5DFDCB] font-display font-bold uppercase tracking-[0.2em] text-xs">
                      {t.page.statsTitle}
                    </h3>
                  </div>

                  <div className="grid sm:grid-cols-2 lg:grid-cols-[minmax(120px,0.7fr)_repeat(4,minmax(0,1fr))] gap-3">
                    <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                      <div className="text-white/50 text-xs font-display uppercase">{t.page.fields.apps}</div>
                      <div className="text-white font-display font-bold mt-1">
                        {playerEventStats.apps !== null && playerEventStats.apps !== undefined
                          ? String(Math.round(playerEventStats.apps))
                          : "-"}
                      </div>
                    </div>
                    <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                      <div className="text-white/50 text-xs font-display uppercase">{t.page.fields.totFxPts}</div>
                      <div className="text-white font-display font-bold mt-1">{formatNumber(playerEventStats.totFxPts, 2)}</div>
                    </div>
                    <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                      <div className="text-white/50 text-xs font-display uppercase">{t.page.fields.avFxPts}</div>
                      <div className="text-white font-display font-bold mt-1">{formatNumber(playerEventStats.avFxPts, 2)}</div>
                    </div>
                    <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                      <div className="text-white/50 text-xs font-display uppercase">{t.page.fields.totFxBns}</div>
                      <div className="text-white font-display font-bold mt-1">{formatNumber(playerEventStats.totFxBns, 2)}</div>
                    </div>
                    <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                      <div className="text-white/50 text-xs font-display uppercase">{t.page.fields.avFxBns}</div>
                      <div className="text-white font-display font-bold mt-1">{formatNumber(playerEventStats.avFxBns, 2)}</div>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  className="bg-[#140623]/80 backdrop-blur-xl border border-white/10 rounded-2xl p-4 md:p-6"
                >
                  <h2 className="text-white font-display font-black text-xl uppercase mb-4">
                    {t.page.table.title}
                  </h2>
                  <div className="hidden md:grid grid-cols-8 gap-3 text-xs font-display font-bold uppercase text-white/60 border-b border-white/10 pb-2">
                    <div>{t.page.table.eventUnit}</div>
                    <div>{t.page.table.match}</div>
                    <div className="text-right">{t.page.table.cost}</div>
                    <div className="text-right">{t.page.table.bonus}</div>
                    <div className="text-right">{t.page.table.pointsIndv}</div>
                    <div className="text-right">{t.page.table.pointsTeam}</div>
                    <div className="text-right">{t.page.table.pointsTotal}</div>
                    <div className="text-center">{t.page.table.official}</div>
                  </div>

                  {participationRows.map((row) => (
                    <div key={row._row_key} className="border-b border-white/5 py-3">
                      <div className="hidden md:grid grid-cols-8 gap-3 items-center text-sm">
                        <div className="text-white/80 font-display font-bold">{row.event_unit_id}</div>
                        <div className="text-white/70 font-sans flex items-center gap-0.5">{renderMatchCode(row)}</div>
                        <div className="text-right text-white/80">{formatNumber(row.event_unit_cost, 1)}</div>
                        <div className="text-right text-white/80">{formatNumber(row.athlete_unit_bonus_indv, 1)}</div>
                        <div className="text-right text-white/80">{formatNumber(row.athlete_unit_points_indv, 1)}</div>
                        <div className="text-right text-white/80">{formatNumber(row.athlete_unit_points_team, 1)}</div>
                        <div className="text-right text-white font-display font-bold">{formatNumber(row.athlete_unit_points, 1)}</div>
                        <div className="text-center text-lg">{Number(row.is_ended) === 1 ? "✅" : ""}</div>
                      </div>

                      <div className="md:hidden rounded-xl border border-white/10 bg-black/20 p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-white font-display font-bold text-sm">
                            {t.page.table.eventUnit} {row.event_unit_id}
                          </div>
                          <div className="text-white/80 font-sans flex items-center gap-0.5">{renderMatchCode(row)}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="text-white/60">{t.page.table.cost}: <span className="text-white">{formatNumber(row.event_unit_cost, 1)}</span></div>
                          <div className="text-white/60">{t.page.table.bonus}: <span className="text-white">{formatNumber(row.athlete_unit_bonus_indv, 1)}</span></div>
                          <div className="text-white/60">{t.page.table.pointsIndv}: <span className="text-white">{formatNumber(row.athlete_unit_points_indv, 1)}</span></div>
                          <div className="text-white/60">{t.page.table.pointsTeam}: <span className="text-white">{formatNumber(row.athlete_unit_points_team, 1)}</span></div>
                          <div className="text-white/60">{t.page.table.pointsTotal}: <span className="text-white">{formatNumber(row.athlete_unit_points, 1)}</span></div>
                          <div className="text-white/60">{t.page.table.official}: <span className="text-white">{Number(row.is_ended) === 1 ? "✅" : ""}</span></div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {participationRows.length === 0 && (
                    <div className="py-8 text-center text-white/60 font-sans text-sm">
                      {lang === "it" ? "Nessun dato matchday disponibile." : "No matchday data available."}
                    </div>
                  )}
                </motion.div>
              </>
            )}
          </div>
        </main>
        <Toaster position="bottom-center" theme="dark" />
      </div>
    </>
  );
};

export default PlayerProfilePage;
